import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import type { TooltipRenderProps } from "react-joyride";
import { useAppSelector } from "redux/hooks";
import { selectScreen } from "redux/appSlice";
import { tourStylesFor } from "../styles/tour.styles";
import { TourChevronRightIcon, TourCloseIcon } from "./TourIcons";

const TourTooltip = ({
  index,
  isLastStep,
  size,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
}: TooltipRenderProps) => {
  const isMobile = useAppSelector(selectScreen);
  const styles = tourStylesFor(isMobile);
  const { closeIconSize, chevronSize, maxDots } = styles.metrics;

  const titleId = `tour-step-title-${index}`;
  const contentId = `tour-step-content-${index}`;
  const counterId = `tour-step-counter-${index}`;
  const isFirstStep = index === 0;

  const dotCount = Math.min(size, maxDots);
  const activeDot = Math.min(index, dotCount - 1);

  return (
    <Paper
      elevation={0}
      {...tooltipProps}
      aria-labelledby={titleId}
      aria-describedby={`${contentId} ${counterId}`}
      sx={styles.card}
    >
      <IconButton {...closeProps} sx={styles.closeButton}>
        <TourCloseIcon sx={{ width: closeIconSize, height: closeIconSize }} />
      </IconButton>

      <Typography id={titleId} component="h2" sx={styles.title}>
        {step.title}
      </Typography>

      <Typography id={contentId} component="p" sx={styles.content}>
        {step.content}
      </Typography>

      <Box sx={styles.footer}>
        <Box sx={styles.progressGroup}>
          <Box sx={styles.dotRow} aria-hidden="true">
            {Array.from({ length: dotCount }).map((_, dotIndex) => (
              <Box key={dotIndex} sx={styles.dot(dotIndex === activeDot)} />
            ))}
          </Box>
          <Typography
            id={counterId}
            component="span"
            aria-label={`Step ${index + 1} of ${size}`}
            sx={styles.counter}
          >
            {`${index + 1} of ${size}`}
          </Typography>
        </Box>

        <Box sx={styles.actions}>
          {!isFirstStep && (
            <Button {...backProps} disableRipple sx={styles.ghostButton}>
              Back
            </Button>
          )}
          {!isLastStep && (
            <Button {...skipProps} disableRipple sx={styles.ghostButton}>
              Skip
            </Button>
          )}
          <Button
            {...primaryProps}
            disableRipple
            disableElevation
            sx={styles.primaryButton}
          >
            {isLastStep ? "Done" : "Next"}
            {!isLastStep && (
              <TourChevronRightIcon
                sx={{ width: chevronSize, height: chevronSize }}
              />
            )}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default TourTooltip;
